/**
 * The 3D world runs in a same-origin iframe (#world-iframe, components/World/PlatformWorld.web.js), so a drag on the
 * world never reaches the page's own listeners: measured on DEV (2026-10-10), the tour's "Hold the left mouse button
 * and drag to look around" could not be completed. The tour listens inside the frame as well. The frame is replaced
 * whenever the world changes, so the current one is looked up again every `everyMs`; a frame that cannot be read
 * (another origin) is simply skipped.
 */
export function listenInWorldFrame(
  listeners,
  {
    getFrame = () => (typeof document === 'undefined' ? null : document.getElementById('world-iframe')),
    everyMs = 1000,
  } = {},
) {
  let attached = null;
  const detach = () => {
    if (!attached) return;
    try {
      Object.entries(listeners).forEach(([type, listener]) => attached.removeEventListener(type, listener, true));
    } catch (_) {
      // The frame is gone or no longer readable: nothing left to remove.
    }
    attached = null;
  };
  const attach = () => {
    let frameWindow = null;
    try {
      const frame = getFrame();
      frameWindow = frame && frame.contentWindow;
      if (frameWindow) frameWindow.document; // throws for a frame from another origin
    } catch (_) {
      frameWindow = null;
    }
    if (frameWindow === attached) return;
    detach();
    if (!frameWindow) return;
    try {
      Object.entries(listeners).forEach(([type, listener]) => frameWindow.addEventListener(type, listener, true));
      attached = frameWindow;
    } catch (_) {
      attached = null;
    }
  };
  attach();
  const timer = setInterval(attach, everyMs);
  return () => {
    clearInterval(timer);
    detach();
  };
}
